/**
 * Branded sign-in / onboarding for Type-2 PROJECT USERS (tenant-scoped end-users
 * who are not Twasta accounts). One modal, several modes:
 *   - login    : email + password (+ "Forgot password?", + register if allowed)
 *   - register : self-registration (email + password the user picks)
 *   - forgot   : request a password-reset email
 *   - reset    : set a new password from an emailed token (?pu_reset=…)
 *   - accept   : accept an invite by setting your own password (?pu_accept=…)
 *
 * The header shows the APP's name/icon/accent so the user knows what they're
 * signing into. Twasta users may also "Sign in with Twasta" — but password
 * reset/change is disabled for them here (they manage it in Twasta).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { startTwastaSso } from '@/api/sso'
import { getHostBinding } from '@/config/hostBinding'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  siteAuthConfig, siteLogin, siteRegister,
  projectUserAccept, projectUserForgotPassword, projectUserResetPassword,
  projectUserInviteInfo, siteMfaSignInApi,
  type SiteAuthInfo, type SiteUser, type SiteRegisterResult,
} from '@/api/website'
import { MFA_LIGHT_VARS, MfaSignInStep, parkedStep, type MfaStep } from '@twasta/page-runtime/shell'
import SitePaymentStep from './SitePaymentStep'

type Mode = 'login' | 'register' | 'forgot' | 'reset' | 'accept'

export default function ProjectAuthModal({
  userId, project, onClose, onAuthed, initialMode = 'login', token,
}: {
  userId: string
  project: string
  onClose: () => void
  onAuthed: (token: string, user: SiteUser) => void
  initialMode?: Mode
  token?: string
}) {
  const [info, setInfo] = useState<SiteAuthInfo | null>(null)
  const [mode, setMode] = useState<Mode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Set when the application charges an access fee: the account exists but
  // holds no roles until this order is paid.
  const [pendingPayment, setPendingPayment] = useState<SiteRegisterResult['order'] | null>(null)
  const [done, setDone] = useState<string | null>(null)
  // A sign-in parked on its second factor (code, or setup when required).
  const [mfaStep, setMfaStep] = useState<MfaStep | null>(null)
  const mfaApi = useMemo(() => siteMfaSignInApi(userId, project), [userId, project])
  const finish = useCallback((data: { token: string; user: SiteUser; mfa_enrol_by?: string }) => {
    if (data.mfa_enrol_by) {
      toast(`Two-factor sign-in is required — set it up from your account menu before ${new Date(data.mfa_enrol_by).toLocaleDateString()}.`, { duration: 8000 })
    }
    onAuthed(data.token, data.user)
  }, [onAuthed])

  useEffect(() => {
    siteAuthConfig(userId, project).then((r) => setInfo(r.data)).catch(() => setInfo(null))
    if (token && initialMode === 'accept') {
      projectUserInviteInfo(userId, project, token)
        .then((r) => { if (r.data.email) setEmail(r.data.email); if (!r.data.valid) setErr('This invitation is invalid or has expired.') })
        .catch(() => setErr('This invitation could not be loaded.'))
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [userId, project, token, initialMode, onClose])

  const branding = useMemo(() => ({
    name: info?.appName || project,
    icon: info?.appIcon || null,
    accent: info?.appThemeColor || '#4f46e5',
  }), [info, project])

  const allowRegister = !!info?.allowRegistration
  /**
   * "Sign in with Twasta" — two different journeys, one button.
   *
   * `info.twastaSigninUrl` is only the FLAG here: it says this application
   * accepts Twasta accounts at all. Where the button goes depends on which
   * origin the person is standing on.
   *
   * On the PLATFORM's own address, a session is a session: the ordinary
   * sign-in page, returning to this exact page. (It used to link straight at
   * `/api/auth/runtime-authorize`, which is the DEPLOYED-runtime bearer
   * handoff — it answered 422 without a `redirect_uri`, and its token is not
   * the kind of session this view needs.)
   *
   * On an application's OWN domain, the platform's cookie cannot be used —
   * cookies belong to an origin — so this starts the authorization-code
   * handover instead: out to the platform, back with a one-time code, and a
   * session minted for THIS host. See `api/sso`.
   */
  const hostBinding = getHostBinding()
  const here = `${window.location.pathname}${window.location.search}`
  const twastaSignIn = useMemo(() => {
    if (!info?.twastaSigninUrl) return null
    if (hostBinding) return () => startTwastaSso(hostBinding, here)
    return () => { window.location.assign(`/login?next=${encodeURIComponent(here)}`) }
  }, [info?.twastaSigninUrl, hostBinding, here])

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null); setBusy(true)
    try {
      if (mode === 'login') {
        const res = await siteLogin(userId, project, email.trim(), password)
        const step = parkedStep(res.data)
        if (step) { setMfaStep(step); return }
        finish(res.data)
        toast.success('Signed in')
      } else if (mode === 'register') {
        const res = await siteRegister(userId, project, email.trim(), password, email.trim())
        if (res.data.payment_required && res.data.order) {
          // A fee applies. The account was created without access; the payment
          // step is what materializes it.
          setPendingPayment(res.data.order)
          return
        }
        const regStep = parkedStep(res.data)
        if (regStep) { setMfaStep(regStep); return }
        finish({ ...res.data, token: res.data.token! })
        toast.success('Welcome!')
      } else if (mode === 'accept') {
        const res = await projectUserAccept(userId, project, token || '', password)
        const accStep = parkedStep(res.data)
        if (accStep) { setMfaStep(accStep); return }
        finish(res.data)
        toast.success('Welcome!')
      } else if (mode === 'reset') {
        await projectUserResetPassword(userId, project, token || '', password)
        setDone('Your password has been reset. You can sign in now.')
        setMode('login'); setPassword('')
      } else if (mode === 'forgot') {
        await projectUserForgotPassword(userId, project, email.trim())
        setDone('If that email is registered, a reset link is on its way.')
      }
    } catch (e2: unknown) {
      setErr((e2 as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || 'Something went wrong. Please try again.')
    } finally { setBusy(false) }
  }, [mode, userId, project, email, password, token, finish])

  const title = mode === 'register' ? `Create your ${branding.name} account`
    : mode === 'forgot' ? 'Reset your password'
    : mode === 'reset' ? 'Set a new password'
    : mode === 'accept' ? `Join ${branding.name}`
    : `Sign in to ${branding.name}`

  const submitLabel = busy ? 'Please wait…'
    : mode === 'register' ? 'Create account'
    : mode === 'forgot' ? 'Send reset link'
    : mode === 'reset' ? 'Set password'
    : mode === 'accept' ? 'Set password & continue'
    : 'Sign in'

  const showEmail = mode !== 'reset' && mode !== 'accept'
  const showPassword = mode !== 'forgot'

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 11000, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5rem 1rem', overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 400, background: '#fff', color: '#0f172a', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        {/* Branded header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0' }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, background: branding.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span className="material-icons" style={{ fontSize: 21, color: '#fff' }}>{branding.icon || 'apps'}</span>
          </span>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, flex: 1, minWidth: 0, color: '#0f172a' }}>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}><X size={20} /></button>
        </div>

        {mfaStep ? (
          <div style={{ ...MFA_LIGHT_VARS, padding: '1.25rem' }}>
            <MfaSignInStep
              step={mfaStep}
              api={mfaApi}
              onSignedIn={(data) => { setMfaStep(null); finish(data); toast.success('Signed in') }}
              onCancel={() => { setMfaStep(null); setPassword('') }}
            />
          </div>
        ) : pendingPayment ? (
          <div style={{ padding: '1.25rem' }}>
            <SitePaymentStep
              appName={branding.name}
              pending={pendingPayment}
              onPaid={() => {
                // Access is live now, but this browser has no session yet — the
                // account was created before payment. Sending them to sign in is
                // the honest next step.
                setPendingPayment(null)
                setDone('Payment received. Sign in to start using the application.')
                setMode('login')
                setPassword('')
              }}
              onCancel={() => {
                setPendingPayment(null)
                setDone('Your account is saved. Sign in any time to finish paying.')
                setMode('login')
                setPassword('')
              }}
            />
          </div>
        ) : (
        <form onSubmit={submit} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {email && (mode === 'accept' || mode === 'reset') && (
            <div style={{ fontSize: 14, color: '#475569' }}>For <strong>{email}</strong></div>
          )}
          {showEmail && (
            <input style={inp} type="email" placeholder="Email" autoComplete="email" autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
          {showPassword && (
            <input style={inp} type="password"
              placeholder={mode === 'login' ? 'Password' : 'New password (min 8 characters)'}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              autoFocus={!showEmail}
              value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
          {done && <div style={{ color: '#047857', fontSize: 14 }}>{done}</div>}
          {err && <div style={{ color: '#dc2626', fontSize: 14 }}>{err}</div>}
          <button type="submit" disabled={busy || (showEmail && !email) || (showPassword && !password)}
            style={{ padding: '0.55rem 1rem', borderRadius: 8, background: branding.accent, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, opacity: busy ? 0.6 : 1 }}>
            {submitLabel}
          </button>

          {/* Mode switches */}
          {mode === 'login' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <button type="button" onClick={() => { setErr(null); setDone(null); setMode('forgot') }} style={linkBtn}>Forgot password?</button>
              {allowRegister && (
                <button type="button" onClick={() => { setErr(null); setDone(null); setMode('register') }} style={linkBtn}>Create an account</button>
              )}
            </div>
          )}
          {(mode === 'register' || mode === 'forgot') && (
            <button type="button" onClick={() => { setErr(null); setDone(null); setMode('login') }} style={{ ...linkBtn, alignSelf: 'flex-start' }}>← Back to sign in</button>
          )}

          {/* Twasta sign-in (Type-1) — password reset handled in Twasta. */}
          {mode === 'login' && twastaSignIn && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0' }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                <span style={{ fontSize: 13, color: '#94a3b8' }}>OR</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              </div>
              <button type="button" onClick={twastaSignIn} style={{ textAlign: 'center', padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>
                Sign in with Twasta
              </button>
              <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
                Twasta users manage their password in Twasta.
              </div>
            </>
          )}
        </form>
        )}
      </div>
    </div>
  )
}

const inp: React.CSSProperties = {
  padding: '0.5rem 0.65rem', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 15,
  width: '100%', boxSizing: 'border-box', color: '#0f172a', background: '#fff',
}

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: 14, padding: 0,
}
